/**
 * viewportClipObjectsSynthesize — N7 VPCLIP-BOUNDARY-EXPORT.
 *
 * Synthesizes the OBJECTS-section SPATIAL_FILTER + owning extension-dictionary
 * / ACAD_FILTER DICTIONARY chain for an APP-AUTHORED non-rectangular VPCLIP
 * boundary, so it survives export/reload instead of being silently dropped.
 *
 * ## The gap this closes
 *
 * `applyViewportClipSetPolygon` (command-engine `viewportCommands.ts`) already
 * authors a full typed clip on the VIEWPORT entity — `clipBoundaryPaperSpace`
 * (the paper-space boundary), the derived `clipPath` (model-space projection),
 * and the group-90 `VIEWPORT_NONRECT_CLIP_BIT`. When the boundary came from an
 * EXISTING entity (`VPCLIP <vp> O <object>`), that object's own handle is
 * recorded on `clippingBoundaryHandle` and the clip round-trips today via the
 * plain group-340 passthrough (`dxfBridge.entityConversion.ts`). But when the
 * boundary is EXPLICIT POINTS with no matched object (`VPCLIP <vp> Polygonal`,
 * drawn/scripted vertices), there is no existing entity to reference — the
 * viewport stays "handle-less", and `maskOrphanNonRectClipBit`
 * (`viewportGroup90ClipBit.ts`) — correctly, per its OWN contract — clears the
 * orphan non-rect bit rather than emit a corrupt flag with no boundary to pair
 * with. Net effect: the clip renders/undoes correctly in-session but is lost on
 * export/reload.
 *
 * This module closes that gap by synthesizing a REAL SPATIAL_FILTER object (the
 * SAME OBJECTS mechanism `S-OBJECTS-SPATIAL-FILTER` / `S-VIEWPORT-CLIP-RESOLVE`
 * already parse on IMPORT — ext-dict → `ACAD_FILTER` dict → `SPATIAL` entry →
 * SPATIAL_FILTER, verified against the ezdxf `xclip.py` chain, see
 * `handleResolution.resolveViewportClip`) at EXPORT TIME, from the viewport's
 * CURRENT typed clip fields — mirroring how `dxfBridge.annotativeExport
 * .buildAnnotativeObjectsGraph` re-synthesizes the annotative OBJECTS graph
 * from typed `annotativeContexts` on every export (the established pattern for
 * "typed model owns the semantics, dxf-io owns the byte format, the export
 * orchestrator re-derives the OBJECTS graph fresh every save" — CLAUDE.md
 * ПРИОРИТЕТ #2.1: export is a DERIVED representation, never a second source of
 * truth persisted mid-session on `document.rawObjectGroups`).
 *
 * ## Boundary coordinate frame (verified against
 * `dxfBridge.viewportClip.test.ts`'s golden fixture)
 *
 * The import resolver (`resolveViewportClip` / `expandSpatialFilterClipPolygon`)
 * sets the resolved `clipPath` DIRECTLY from a SPATIAL_FILTER's `boundaryPoints`
 * transformed ONLY by the (typically identity, for a plain paper-space VIEWPORT)
 * `inverseInsertMatrix` — it does NOT additionally project through the
 * viewport's paper→model view transform the way a native-340 boundary-ENTITY
 * clip does (`paperToViewportModel` in `viewportClipFinalize.ts`). So on
 * export the boundary points written to the SPATIAL_FILTER must already be the
 * CURRENT model-space clip polygon (`recomputeNativeClipPath(vp)`, the SAME
 * paper→model projection the in-session renderer/undo path uses) — writing the
 * raw `clipBoundaryPaperSpace` verbatim would misplace the clip on reimport for
 * any viewport with a non-trivial view (pan/zoom/scale/twist). The matrix pair
 * is IDENTITY (no block-insert transform applies to a plain paper-space
 * viewport clip — `handleResolution.ts`'s own doc comment on
 * `transformByInverseInsertMatrix`: "step (1) alone yields the WCS (paper-
 * space) clip polygon").
 *
 * One documented, ACCEPTED semantic consequence of choosing the SPATIAL_FILTER
 * representation: unlike an in-session app-authored clip (paper-fixed, tracks
 * the viewport's view via `clipBoundaryPaperSpace` + `recomputeNativeClipPath`
 * on every pan/zoom), a SPATIAL_FILTER-sourced clip is WCS-fixed after
 * reimport (`viewportClipFinalize.ts`: "A SPATIAL_FILTER clip is fixed in
 * MODEL/WCS space… must NOT be recomputed"). This matches how AutoCAD's own
 * SPATIAL_FILTER-backed VPCLIP behaves and is the same representation this
 * codebase already round-trips for an IMPORTED clip — the boundary is captured
 * as of THIS export, exactly like a real AutoCAD save.
 *
 * ## Group 340 is intentionally left ABSENT (@codex P1, N7 PR #556 round 3)
 *
 * DXF group 340 on `AcDbViewport` is a hard pointer to a GRAPHICAL paper-space
 * boundary ENTITY (LWPOLYLINE / CIRCLE / ELLIPSE / closed SPLINE — see
 * `model.ts`'s own `clippingBoundaryHandle` field doc + the Autodesk DXF
 * reference), used ONLY by the native-340 mechanism ("Select clipping object" —
 * `applyViewportClipSetPolygon`'s object-matched path, which already round-
 * trips via the plain group-340 passthrough). It is NOT a pointer to a
 * SPATIAL_FILTER object — the SPATIAL_FILTER chain is reached independently via
 * the entity's OWN extension dictionary (`common.extensionDictionaryHandle`,
 * group 360) → `ACAD_FILTER` dict → `SPATIAL` entry, exactly as
 * `resolveViewportClip` walks it. The golden fixture
 * (`dxfBridge.viewportClip.test.ts`'s `CLIP_DXF`) confirms this: its
 * SPATIAL_FILTER-sourced VIEWPORT carries NO group 340 at all. An earlier
 * version of this module pointed 340 at the synthesized SPATIAL_FILTER's OWN
 * handle — internally harmless (the internal reimport resolves the ext-dict
 * chain FIRST and skips native-340 resolution once `clipPath` is already set),
 * but type-invalid for any OTHER DXF reader, which could audit-flag or discard
 * a 340 pointing at a non-graphical OBJECTS-section record. This module now
 * synthesizes the clip signaled ONLY by the ext-dict chain + the group-90
 * non-rect bit — never a fabricated 340.
 *
 * ## Ext-dict reuse
 *
 * A viewport may already advertise `extensionDictionaryHandle` for UNRELATED
 * content (an annotation-scale XRECORD) or a PRIOR imported clip whose
 * `ACAD_FILTER` entry was already stripped by `detachInvalidatedViewportClip
 * Objects` on re-clip. `attachAcadFilterEntry` (`viewportClipObjectsStrip.ts`)
 * surgically re-attaches a fresh `3/ACAD_FILTER` entry onto that EXISTING raw
 * DICTIONARY record, preserving every other entry verbatim. Only a viewport
 * with NO ext-dict at all gets a brand-new one synthesized.
 */

import type {
  DxfDictionaryEntry,
  DxfEntity,
  DxfSpatialFilter,
} from "@dxf-ai-editor/dxf-io";

import {
  makeHandleMinter,
  type HandleMinter,
} from "./dxfBridge.annotativeExport";
import { maxHexHandleValue } from "./dxfBridge.handleSeed";
import { recomputeNativeClipPath } from "./viewportClipFinalize";
import { attachAcadFilterEntry } from "./viewportClipObjectsStrip";
import { NONRECT_CLIP_BIT } from "./viewportGroup90ClipBit";
import type { DrawingDocument, DrawingEntity, ViewportEntity } from "./model";

/**
 * Identity 4×3 matrix (12 reals, the DXF group-40×12 layout) — no block-insert
 * transform applies to a plain paper-space VIEWPORT's own SPATIAL_FILTER (see
 * module header). Shared by BOTH the inverse and forward matrix fields.
 */
const IDENTITY_MATRIX_12: readonly number[] = [
  1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0,
];

/** A raw DXF group pair `{code, value}` — mirrors `document.rawObjectGroups`. */
type RawGroup = { code: number; value: string };

export type SynthesizedViewportClip = {
  /** Minted (or reused) extension-dictionary handle for THIS viewport. */
  extDictHandle: string;
  /** True when `extDictHandle` is NEWLY minted (must be stamped onto the
   *  emitted entity's `common.extensionDictionaryHandle` — a REUSED handle is
   *  already advertised there by the ordinary common-props projection). */
  extDictIsNew: boolean;
  /** Minted SPATIAL_FILTER handle. NEVER stamped onto the viewport's group
   *  340 (@codex P1 — see module header "Group 340 is intentionally left
   *  ABSENT"); kept here for collision-tracking (folded into `emittedHandles`)
   *  and test/introspection visibility. */
  spatialFilterHandle: string;
};

export type ViewportClipObjectsSynthesisResult = {
  /** `ViewportEntity.id` → synthesis outcome, for every viewport synthesized. */
  byViewportId: Map<string, SynthesizedViewportClip>;
  /** New DICTIONARY records to append to `objectsSection.dictionaries`. */
  dictionaries: DxfDictionaryEntry[];
  /** New SPATIAL_FILTER records to append to `objectsSection.spatialFilters`
   *  (`serializeObjectsSection` already flushes a sentinel-less typed filter —
   *  see writer.ts's "finding 7" flush loop — no writer change needed). */
  spatialFilters: DxfSpatialFilter[];
  /** `rawObjectGroups`, patched for every REUSED ext-dict (ACAD_FILTER entry
   *  inserted in place). Same reference as the input when nothing changed. */
  rawObjectGroups: RawGroup[];
};

/**
 * True when a paper-space VIEWPORT carries an app-authored, not-yet-exportable
 * non-rectangular clip boundary: `applyViewportClipSetPolygon` set
 * `clipBoundaryPaperSpace` (≥3 vertices — the command rejects a degenerate
 * polygon before mutating) but recorded NO object handle (`VPCLIP … O <obj>`
 * always sets one; explicit-points authoring never does). `clipBoundaryKind` is
 * — by construction, per the mutually-exclusive model.ts field contract —
 * always absent alongside `clipBoundaryPaperSpace` (it is the SPATIAL_FILTER-
 * import-only companion field), checked here anyway for defensive clarity.
 */
function needsClipSynthesis(vp: ViewportEntity): boolean {
  return (
    vp.clipBoundaryPaperSpace !== undefined &&
    vp.clipBoundaryPaperSpace.length >= 3 &&
    vp.clippingBoundaryHandle === undefined &&
    vp.clipBoundaryKind === undefined
  );
}

/**
 * Every paper-space VIEWPORT entity across all spaces (VIEWPORTs never live in
 * model space or inside block definitions — mirrors
 * `viewportClipFinalize.collectPaperViewports`, kept local to avoid a
 * cross-module import for one small scan).
 *
 * @codex P2 — reads `entitiesSnapshot.get(space.id) ?? space.entities` for
 * EACH space, exactly like `buildAnnotativeObjectsGraph` ("F2 — thread
 * entitiesSnapshot so the annotative graph reads from the SAME per-space
 * source the ENTITIES writer uses") and `buildBlocksSectionFromDrawingBlocks`
 * ("F1 #314 round-2 — thread the SAME effectiveSnapshot"). When the caller
 * routes the export through the compact-cache read-through wrapper, a
 * snapshot-only or snapshot-UPDATED viewport's `clipBoundaryPaperSpace` is
 * invisible on the plain `document.spaces[].entities` getter (which
 * materialises a FRESH — potentially stale relative to the snapshot — array
 * on every access); scanning it directly would silently skip that viewport's
 * clip synthesis.
 */
function collectPaperViewports(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): ViewportEntity[] {
  const out: ViewportEntity[] = [];
  for (const space of document.spaces) {
    const sourceEntities = entitiesSnapshot?.get(space.id) ?? space.entities;
    for (const entity of sourceEntities) {
      if (entity.type === "Viewport") out.push(entity);
    }
  }
  return out;
}

/**
 * Synthesize the SPATIAL_FILTER + owning DICTIONARY chain for every paper-space
 * viewport carrying an app-authored, handle-less non-rectangular clip
 * (`needsClipSynthesis`). Pure over its inputs: returns fresh arrays / a
 * (possibly patched) `rawObjectGroups`, never mutates `document` or the
 * caller's `rawObjectGroups` array in place.
 *
 * @param document        the document whose paper-space viewports are scanned
 * @param rawObjectGroups the export orchestrator's CURRENT flat OBJECTS
 *                        passthrough (threaded through the same sequential
 *                        patch pipeline as `stripPreservedScaleListDict` /
 *                        `replaceDictionaryChildEntries`)
 * @param mintHandle      a document-wide collision-checked minter (seeded from
 *                        every handle the export has emitted so far)
 * @param entitiesSnapshot the orchestrator's optional per-space compact-cache
 *                        snapshot (`ExportDrawingDocumentToDxfStringOptions
 *                        .entitiesSnapshot`) — threaded through so a viewport
 *                        living ONLY in the snapshot (or whose clip fields were
 *                        updated there) is still scanned (@codex P2).
 */
export function synthesizeAuthoredViewportClipObjects(
  document: DrawingDocument,
  rawObjectGroups: ReadonlyArray<RawGroup>,
  mintHandle: HandleMinter,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): ViewportClipObjectsSynthesisResult {
  const byViewportId = new Map<string, SynthesizedViewportClip>();
  const dictionaries: DxfDictionaryEntry[] = [];
  const spatialFilters: DxfSpatialFilter[] = [];
  let currentRaw: RawGroup[] = rawObjectGroups as RawGroup[];

  for (const vp of collectPaperViewports(document, entitiesSnapshot)) {
    if (!needsClipSynthesis(vp)) continue;

    const filterDictHandle = mintHandle();
    const spatialFilterHandle = mintHandle();

    let extDictHandle: string;
    let extDictIsNew: boolean;
    if (vp.extensionDictionaryHandle) {
      const attach = attachAcadFilterEntry(
        currentRaw,
        vp.extensionDictionaryHandle,
        filterDictHandle,
      );
      if (!attach.attached) {
        // Defensive: the advertised ext-dict handle does not resolve to a real
        // DICTIONARY record (corrupted / unusual source), or it unexpectedly
        // already carries an ACAD_FILTER entry (contradicts the synthesis gate
        // — would mean `resolveViewportClip` should already have set
        // `clipBoundaryKind` on import). Skip rather than risk corrupting an
        // unknown structure; the clip stays masked exactly like before this
        // module existed — never WORSE than the pre-fix behaviour.
        continue;
      }
      currentRaw = attach.rawObjectGroups;
      extDictHandle = vp.extensionDictionaryHandle;
      extDictIsNew = false;
    } else {
      extDictHandle = mintHandle();
      extDictIsNew = true;
      dictionaries.push({
        kind: "dictionary",
        handle: extDictHandle,
        ownerHandle: vp.id,
        hardOwned: true,
        cloning: 1,
        entries: [
          { name: "ACAD_FILTER", handle: filterDictHandle, isHard: false },
        ],
      });
    }

    dictionaries.push({
      kind: "dictionary",
      handle: filterDictHandle,
      ownerHandle: extDictHandle,
      hardOwned: true,
      cloning: 1,
      entries: [{ name: "SPATIAL", handle: spatialFilterHandle, isHard: false }],
    });

    // The current model-space clip polygon — recomputed fresh from the
    // retained paper-space boundary through the viewport's CURRENT view
    // transform (the SAME projection the in-session renderer/undo path uses),
    // so a view change between authoring and export is captured correctly.
    // `clipBoundaryPaperSpace` has ≥3 vertices (the gate above), so this always
    // returns a ≥3-point polygon — the `?? []` defensive fallback is
    // unreachable in practice.
    const boundary = recomputeNativeClipPath(vp) ?? vp.clipPath ?? [];

    spatialFilters.push({
      handle: spatialFilterHandle,
      ownerHandle: filterDictHandle,
      boundaryPoints: boundary.map((p) => ({ x: p.x, y: p.y })),
      clipActive: true,
      frontClip: { enabled: false, distance: 0 },
      backClip: { enabled: false, distance: 0 },
      inverseInsertMatrix: [...IDENTITY_MATRIX_12],
      transformMatrix: [...IDENTITY_MATRIX_12],
    });

    byViewportId.set(vp.id, {
      extDictHandle,
      extDictIsNew,
      spatialFilterHandle,
    });
  }

  return {
    byViewportId,
    dictionaries,
    spatialFilters,
    rawObjectGroups: currentRaw,
  };
}

export type ApplyViewportClipBoundarySynthesisParams = {
  document: DrawingDocument;
  /** The orchestrator's top-level ENTITIES-section list (mutated in place —
   *  a synthesized viewport's `statusBitFlags` / `common.extensionDictionary
   *  Handle` are stamped directly onto the ALREADY-BUILT `DxfEntity`, mirroring
   *  `buildAnnotativeObjectsGraph`'s own post-hoc ext-dict stamp. Group 340 is
   *  intentionally NEVER stamped — see module header "Group 340 is
   *  intentionally left ABSENT", @codex P1). */
  entities: DxfEntity[];
  /** Every BLOCK definition's children (`blocksSection?.blocks ?? []`) — an
   *  EXTRA paper-space tab's VIEWPORT is emitted here, NOT in `entities`
   *  (@codex P2, see module header "Ext-dict reuse" / N7 dispatch notes).
   *  Mutated in place exactly like `entities`. */
  blocksSectionBlocks: ReadonlyArray<{ entities: DxfEntity[] }>;
  /** The orchestrator's CURRENT flat OBJECTS passthrough (sequential patch
   *  pipeline — same contract as `synthesizeAuthoredViewportClipObjects`). */
  objectsRawGroups: RawGroup[];
  /** The orchestrator's document-wide emitted-handle population. Mutated in
   *  place (every minted handle is folded back in) so a LATER minting pass
   *  (e.g. `maybeSynthesizeMLeaderStyleRootDicts`) collision-avoids against
   *  this module's output — mirrors how the annotative / MLEADER passes
   *  already share this one Set. */
  emittedHandles: Set<string>;
  /** Extra handles to seed the minter with that are NOT yet folded into
   *  `emittedHandles` at call time (e.g. `annotativeGraph.mintedExtDictHandles`
   *  — its internal minter does not write back into the shared Set). */
  extraSeedHandles?: Iterable<string>;
  /** @codex P2 — threaded straight through to `synthesizeAuthoredViewportClip
   *  Objects` so a snapshot-only / snapshot-updated viewport is still scanned. */
  entitiesSnapshot?: Map<string, DrawingEntity[]>;
};

export type ApplyViewportClipBoundarySynthesisResult = {
  rawObjectGroups: RawGroup[];
  dictionaries: DxfDictionaryEntry[];
  spatialFilters: DxfSpatialFilter[];
};

/**
 * Document-level orchestration wrapper around {@link
 * synthesizeAuthoredViewportClipObjects}: mints a collision-checked handle
 * range, runs the synthesis, stamps every synthesized viewport's ALREADY-BUILT
 * `DxfEntity` (both top-level `entities` AND every block-emitted EXTRA
 * paper-tab viewport — @codex P2), and folds the minted handles back into the
 * shared `emittedHandles` population. Extracted out of the `dxfBridge.export`
 * orchestrator (which was pushed over the 2000-line `max-lines` ESLint
 * ceiling by the inline version) so the call site there stays a single
 * function call — CLAUDE.md file-size guardrail, barrel-free split (this
 * module already sits on the `drawing-model → dxf-io` edge the orchestrator
 * uses, so no new dependency edge is introduced).
 */
export function applyViewportClipBoundarySynthesis(
  params: ApplyViewportClipBoundarySynthesisParams,
): ApplyViewportClipBoundarySynthesisResult {
  const {
    document,
    entities,
    blocksSectionBlocks,
    objectsRawGroups,
    emittedHandles,
    extraSeedHandles,
    entitiesSnapshot,
  } = params;

  const seedHandles = new Set(emittedHandles);
  for (const h of extraSeedHandles ?? []) seedHandles.add(h.toLowerCase());

  const graph = synthesizeAuthoredViewportClipObjects(
    document,
    objectsRawGroups,
    makeHandleMinter(
      seedHandles,
      Math.max(0x210000, maxHexHandleValue(seedHandles)),
    ),
    entitiesSnapshot,
  );

  if (graph.byViewportId.size > 0) {
    const stampViewportEntity = (entity: DxfEntity): void => {
      if (entity.kind !== "supported-entity" || entity.type !== "VIEWPORT") {
        return;
      }
      const synth = graph.byViewportId.get(entity.common.handle ?? "");
      if (!synth) return;
      if (synth.extDictIsNew) {
        entity.common.extensionDictionaryHandle = synth.extDictHandle;
      }
      // @codex P1 — group 340 is deliberately NEVER set here. It is a hard
      // pointer to a GRAPHICAL boundary entity (native-340 mechanism), not to
      // a SPATIAL_FILTER object; see module header "Group 340 is intentionally
      // left ABSENT". The clip is signaled by `common.extensionDictionaryHandle`
      // (stamped above) + the ext-dict → ACAD_FILTER → SPATIAL chain + the
      // re-ORed group-90 bit below — matching the golden fixture exactly.
      //
      // Re-OR the non-rect bit `toDxfEntity` masked out earlier: at that point
      // (an EARLIER, per-entity pass) the TYPED model viewport had neither a
      // 340 handle nor `clipBoundaryKind` set (the latter is an IMPORT-only
      // field — an app-authored clip never sets it), so `maskOrphanNonRectClip
      // Bit` (`viewportGroup90ClipBit.ts`) correctly treated it as an orphan
      // and cleared the bit. The SPATIAL_FILTER chain synthesized ABOVE now
      // resolves (ext-dict → ACAD_FILTER → SPATIAL), so the flag is a valid
      // pairing again — restore it here, at this LATER document-level pass.
      entity.geometry.statusBitFlags =
        (entity.geometry.statusBitFlags ?? 0) | NONRECT_CLIP_BIT;
    };
    for (const entity of entities) stampViewportEntity(entity);
    for (const block of blocksSectionBlocks) {
      for (const child of block.entities) stampViewportEntity(child);
    }
    for (const d of graph.dictionaries) {
      emittedHandles.add(d.handle.toLowerCase());
    }
    for (const f of graph.spatialFilters) {
      if (f.handle) emittedHandles.add(f.handle.toLowerCase());
    }
  }

  return {
    rawObjectGroups: graph.rawObjectGroups,
    dictionaries: graph.dictionaries,
    spatialFilters: graph.spatialFilters,
  };
}
